import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { Task } from '@repo/shared';
import { useRouter } from '@tanstack/react-router';
import { Brain } from 'lucide-react';
import { toast } from 'sonner';

import { useCreateTextTranslationWithDataset } from '@/api/generated';
import DatasetCombobox from '@/components/dataset-combobox';
import ModelsCombobox from '@/components/models-combobox';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Spinner } from '@/components/ui/spinner';
import {
  type CreateTextTranslationWithDatasetSchema,
  createTextTranslationWithDatasetSchema,
} from '@/routes/(app)/(tasks)/lib/text-translation-schema';

const TextTranslationWithDataset = () => {
  const { mutate, isPending } = useCreateTextTranslationWithDataset();
  const form = useForm<CreateTextTranslationWithDatasetSchema>({
    defaultValues: {
      models: [],
    },
    resolver: zodResolver(createTextTranslationWithDatasetSchema),
  });
  const { t } = useTranslation();

  const router = useRouter();

  const handleSubmit = useCallback(
    (values: CreateTextTranslationWithDatasetSchema) => {
      mutate(
        {
          data: {
            datasetId: values.datasetId,

            models: values.models,
          },
        },
        {
          onSuccess: async () => {
            toast.success(t('clusters.createModelRun.success'));
            await router.navigate({
              to: '/app/clusters',
              search: { datasetId: values.datasetId, isEval: true },
            });
          },
          onError: () => {
            toast.error(t('clusters.createModelRun.error'));
          },
        }
      );
    },
    [mutate, router, t]
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <Card>
          <CardContent className='flex flex-col gap-4'>
            <FormField
              control={form.control}
              name='datasetId'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('tasks.textTranslation.form.dataset.label')}
                  </FormLabel>
                  <FormControl>
                    <DatasetCombobox
                      task={Task.TEXT_TRANSLATION}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='models'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('tasks.textTranslation.form.models.label')}
                  </FormLabel>
                  <ModelsCombobox
                    task={Task.TEXT_TRANSLATION}
                    values={field.value}
                    {...field}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
          <CardFooter>
            <Button
              type='submit'
              disabled={isPending}>
              <Button.Icon side={'left'}>
                {isPending ? <Spinner className='text-inherit' /> : <Brain />}
              </Button.Icon>
              {t('tasks.textTranslation.form.submit')}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </Form>
  );
};

export default TextTranslationWithDataset;
